"use client";

import { useTranslations } from "next-intl";

type SkillCategory = {
  key: "frontendNative" | "backendApis" | "platformIntegrations";
  accent: string;
  items: string[];
};

const skillCategories: SkillCategory[] = [
  {
    key: "frontendNative",
    accent: "text-cyan-300",
    items: [
      "React.js",
      "Next.js",
      "React Native",
      "Expo",
      "Vue.js",
      "HTML5",
      "CSS3",
      "JavaScript",
      "TypeScript",
      "Tailwind CSS",
      "Framer Motion",
      "Redux",
      "Apollo",
      "Liquid",
      "PWA",
      "Shadcn/UI",
    ],
  },
  {
    key: "backendApis",
    accent: "text-violet-300",
    items: [
      "Node.js",
      "NestJS",
      "Express.js",
      "GraphQL",
      "RESTful APIs",
      "Microservices",
      "Serverless",
      "Prisma",
      "Webhooks",
    ],
  },
  {
    key: "platformIntegrations",
    accent: "text-amber-300",
    items: ["Vercel", "Netlify", "N8N", "Stripe", "Strapi CMS", "Eleven Labs"],
  },
];

const allSkillsCount = skillCategories.reduce(
  (total, category) => total + category.items.length,
  0,
);

export default function SkillsPage() {
  const t = useTranslations("skills");

  return (
    <section className="min-h-[80vh] py-12 xl:py-16">
      <div className="container mx-auto">
        <div className="mx-auto max-w-5xl">
          <h1 className="text-4xl xl:text-6xl font-bold tracking-tight text-center">
            {t("title")}
          </h1>
          <p className="mt-4 text-center text-white/70 max-w-3xl mx-auto text-base xl:text-lg">
            {t("description")}
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-center">
              <p className="text-2xl font-bold text-accent">{allSkillsCount}+</p>
              <p className="text-sm text-white/70">{t("technologies")}</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-center">
              <p className="text-2xl font-bold text-accent">20+</p>
              <p className="text-sm text-white/70">{t("projectsCompleted")}</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-center">
              <p className="text-2xl font-bold text-accent">{t("experienceValue")}</p>
              <p className="text-sm text-white/70">{t("experienceLabel")}</p>
            </div>
          </div>

          <div className="mt-10 space-y-6">
            {skillCategories.map((category) => (
              <article
                key={category.key}
                className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.03] p-5 xl:p-6 shadow-[0_0_0_1px_rgba(255,255,255,0.02)]"
              >
                <h2
                  className={`text-xl xl:text-2xl font-semibold mb-4 ${category.accent}`}
                >
                  {t(`categories.${category.key}`)}
                </h2>
                <div className="flex flex-wrap gap-3">
                  {category.items.map((item) => (
                    <span
                      key={item}
                      className="rounded-xl border border-white/10 bg-primary/60 px-4 py-2 text-sm xl:text-base text-white/90 transition-colors duration-300 hover:border-accent/60 hover:text-accent"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
